/**
 * Client for the host-facing seating charts (OrchestratorAPI
 * `/api/groups/{groupId}/seating-charts`). Owner-only server-side.
 *
 * Requests go through the shared orchestrator axios instance, which supplies the
 * bearer token and owns the single 401 recovery path — this module never touches
 * a token.
 *
 * Two things this API expects that are easy to leave out:
 *
 *  - Every write that changes the room carries `If-Match: "<sourceVersion>"`.
 *    Seating is the one Together surface where two hosts genuinely do work at
 *    the same time the night before, and last-write-wins silently discards an
 *    hour of arranging. A 409 version_conflict is the server telling us to
 *    reload, not an error to retry.
 *  - The escape hatches are query flags, not different endpoints: `?swap=true`
 *    exchanges two occupants, `?allowNonAttendee=true` seats someone with no
 *    claim, `?force=true` deletes an occupied table. Each one exists so the
 *    default can stay safe.
 */
import type { AxiosRequestConfig } from 'axios';
import { orchestratorClient } from './groups-api-client';
import type {
  AssignSeatRequest,
  AssignSeatResponse,
  ClearReleasedResponse,
  CreateSeatingChartRequest,
  CreateSeatingTableRequest,
  SeatBatchRequest,
  SeatBatchResponse,
  SeatingChartDetail,
  SeatingChartSummary,
  SeatingTable,
  UpdateSeatingChartRequest,
  UpdateSeatingTableRequest,
  UpdateSeatingTableResponse,
} from '../types/seating';

/** The ETag the chart read returns, quoted, as the API compares it. */
function ifMatch(sourceVersion?: number | null): AxiosRequestConfig {
  if (!Number.isFinite(sourceVersion as number)) return {};
  return { headers: { 'If-Match': `"${sourceVersion}"` } };
}

const chartsPath = (groupId: string) => `/api/groups/${groupId}/seating-charts`;

export const seatingApi = {
  async listCharts(groupId: string, status?: string): Promise<SeatingChartSummary[]> {
    const { data } = await orchestratorClient.get(chartsPath(groupId), {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async createChart(groupId: string, body: CreateSeatingChartRequest): Promise<SeatingChartDetail> {
    const { data } = await orchestratorClient.post(chartsPath(groupId), body);
    return data;
  },

  /**
   * The bulk read: chart, dinner, tables with chairs, candidates, counts, stale
   * seats. Studio always takes the default include set — who is in a chair
   * rides on `seats[]`, so `?include=geometry=false` would return a room with no
   * occupants, whatever the client can recompute.
   */
  async getChart(groupId: string, chartId: string): Promise<SeatingChartDetail> {
    const { data } = await orchestratorClient.get(`${chartsPath(groupId)}/${chartId}`);
    return data;
  },

  async updateChart(
    groupId: string,
    chartId: string,
    body: UpdateSeatingChartRequest,
    sourceVersion?: number,
  ): Promise<SeatingChartDetail> {
    const { data } = await orchestratorClient.patch(
      `${chartsPath(groupId)}/${chartId}`,
      body,
      ifMatch(sourceVersion),
    );
    return data;
  },

  /** Soft: the chart goes to 'archived' and frees its dinner for a fresh one. */
  async archiveChart(groupId: string, chartId: string): Promise<void> {
    await orchestratorClient.delete(`${chartsPath(groupId)}/${chartId}`);
  },

  async createTable(
    groupId: string,
    chartId: string,
    body: CreateSeatingTableRequest,
  ): Promise<SeatingTable> {
    const { data } = await orchestratorClient.post(`${chartsPath(groupId)}/${chartId}/tables`, body);
    return data;
  },

  /**
   * All-or-nothing. "Add fourteen rounds of ten" as fourteen POSTs is fourteen
   * version bumps and fourteen chances to half-build a room.
   */
  async createTables(
    groupId: string,
    chartId: string,
    tables: CreateSeatingTableRequest[],
  ): Promise<{ created: SeatingTable[] }> {
    const { data } = await orchestratorClient.post(
      `${chartsPath(groupId)}/${chartId}/tables/batch`,
      { tables },
    );
    return data;
  },

  /**
   * Resize, reshape, relabel or reorder a table that already exists.
   *
   * Growing a table is unremarkable. Shrinking one is the whole reason this
   * endpoint has a flag: the chairs it removes may have people in them, so by
   * default the API refuses with `seat_index_out_of_range` and the list of who
   * would lose a chair. `reflow` compacts the survivors into the smaller range
   * instead and reports every move — which is a different promise, because it
   * silently changes which physical chair a guest is in and the host may have
   * printed place cards yesterday. Neither is a default the client gets to
   * choose on the host's behalf.
   *
   * Without this, growing a table meant deleting it and unseating everyone.
   */
  async updateTable(
    groupId: string,
    chartId: string,
    tableId: string,
    body: UpdateSeatingTableRequest,
    options: { sourceVersion?: number; reflow?: boolean } = {},
  ): Promise<UpdateSeatingTableResponse> {
    const { data } = await orchestratorClient.patch(
      `${chartsPath(groupId)}/${chartId}/tables/${tableId}`,
      body,
      {
        ...ifMatch(options.sourceVersion),
        params: options.reflow ? { reflow: true } : undefined,
      },
    );
    return data;
  },

  /** Hard delete — a table is layout, not a record. 409s unless `force` when occupied. */
  async deleteTable(
    groupId: string,
    chartId: string,
    tableId: string,
    force = false,
  ): Promise<void> {
    await orchestratorClient.delete(`${chartsPath(groupId)}/${chartId}/tables/${tableId}`, {
      params: force ? { force: true } : undefined,
    });
  },

  /**
   * Sit someone down. Idempotent on an addressable chair, which is why it is a
   * PUT — and why a MOVE is this same call: assigning a guest who is already
   * seated in this chart vacates their old chair in the same transaction and
   * reports it as `vacated`.
   */
  async assignSeat(
    groupId: string,
    chartId: string,
    tableId: string,
    seatIndex: number,
    body: AssignSeatRequest,
    options: { sourceVersion?: number; swap?: boolean; allowNonAttendee?: boolean } = {},
  ): Promise<AssignSeatResponse> {
    const params: Record<string, boolean> = {};
    if (options.swap) params.swap = true;
    if (options.allowNonAttendee) params.allowNonAttendee = true;
    const { data } = await orchestratorClient.put(
      `${chartsPath(groupId)}/${chartId}/tables/${tableId}/seats/${seatIndex}`,
      body,
      { ...ifMatch(options.sourceVersion), params: Object.keys(params).length ? params : undefined },
    );
    return data;
  },

  /** Empties the chair. Never touches the guest's dinner claim. */
  async unassignSeat(
    groupId: string,
    chartId: string,
    tableId: string,
    seatIndex: number,
    sourceVersion?: number,
  ): Promise<void> {
    await orchestratorClient.delete(
      `${chartsPath(groupId)}/${chartId}/tables/${tableId}/seats/${seatIndex}`,
      ifMatch(sourceVersion),
    );
  },

  /**
   * A whole-room rearrangement in one transaction (what auto-seat applies).
   *
   * `sourceVersion` is REQUIRED here, unlike every other write: the server treats
   * a missing If-Match as "no opinion" and would apply a proposal onto a room
   * that has since changed, which is exactly what the preview promised it would
   * not do. A number that is not finite throws rather than sending an
   * unconditional write.
   *
   * A 409 `version_conflict` means someone changed the chart after the proposal
   * was drawn. Nothing was applied; the caller re-reads and proposes again.
   */
  async applySeatBatch(
    groupId: string,
    chartId: string,
    body: SeatBatchRequest,
    sourceVersion: number,
  ): Promise<SeatBatchResponse> {
    if (!Number.isFinite(sourceVersion)) {
      throw new Error('applySeatBatch needs the chart source version the proposal was made from.');
    }
    const { data } = await orchestratorClient.post(
      `${chartsPath(groupId)}/${chartId}/seats/batch`,
      body,
      ifMatch(sourceVersion),
    );
    return data;
  },

  /** The released-claim remedy. Host-initiated, never automatic. */
  async clearReleased(
    groupId: string,
    chartId: string,
    assignmentIds?: string[],
  ): Promise<ClearReleasedResponse> {
    const { data } = await orchestratorClient.post(
      `${chartsPath(groupId)}/${chartId}/assignments/clear-released`,
      assignmentIds?.length ? { assignmentIds } : {},
    );
    return data;
  },
};
